// /settings/whatsapp — WhatsApp Business Cloud API config — G1.4
//
// Per-tenant Meta system-user credentials (AES-256-GCM at rest). OWNER writes.
// SUPER_ADMIN / ADMIN / MODERATOR get a read-only view for support.
// MANAGER / CASHIER are blocked at the route level (see router.tsx).

import { useState, useEffect, useCallback } from 'react';
import {
  PageContainer, Button, Input, Skeleton, ErrorBoundary, useToast,
} from '@bbpos/ui';
import { whatsappService } from '@/services/whatsapp.service';
import { ApiError } from '@/lib/api-client';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_FORM, cn } from '@/constants/design-tokens';
import type { WhatsAppConfigMasked } from '@bbpos/types';

const PHONE_NUMBER_ID_REGEX = /^\d{10,20}$/;

export function WhatsAppConfigPage() {
  const { toast } = useToast();
  const role = useAuthStore((s) => s.user?.role ?? s.profile?.role ?? '');
  const canEdit = role === 'OWNER';
  const isPlatform = role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'MODERATOR';

  const [existing, setExisting] = useState<WhatsAppConfigMasked | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [businessAccountId, setBusinessAccountId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [verifyToken, setVerifyToken] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await whatsappService.getConfig();
      setExisting(cfg);
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        setExisting(null);
      } else if (err instanceof ApiError) {
        toast({ variant: 'error', title: 'Failed to load WhatsApp config', description: err.message });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { document.title = 'WhatsApp — Orio360 Admin'; }, []);

  const handleSave = async () => {
    if (!PHONE_NUMBER_ID_REGEX.test(phoneNumberId.trim())) {
      toast({ variant: 'warning', title: 'Enter a valid Phone Number ID (10–20 digits)' });
      return;
    }
    if (apiKey.trim().length < 20) {
      toast({ variant: 'warning', title: 'Access token looks too short (min 20 chars)' });
      return;
    }
    if (appSecret.trim().length < 16) {
      toast({ variant: 'warning', title: 'App Secret looks too short (min 16 chars)' });
      return;
    }
    if (verifyToken.trim().length < 8) {
      toast({ variant: 'warning', title: 'Verify Token looks too short (min 8 chars)' });
      return;
    }
    setSaving(true);
    try {
      const saved = await whatsappService.upsertConfig({
        phone_number_id: phoneNumberId.trim(),
        business_account_id: businessAccountId.trim() || undefined,
        api_key: apiKey.trim(),
        app_secret: appSecret.trim(),
        verify_token: verifyToken.trim(),
        is_active: true,
      });
      setExisting(saved);
      setPhoneNumberId('');
      setBusinessAccountId('');
      setApiKey('');
      setAppSecret('');
      setVerifyToken('');
      toast({
        variant: 'success',
        title: 'WhatsApp credentials saved',
        description: `Access token ending in ${saved.api_key_last4}`,
      });
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Could not save credentials',
        description: err instanceof ApiError ? err.message : 'Save failed',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await whatsappService.testConfig();
      setTestResult({ ok: res.ok, error: res.error });
      if (res.ok) toast({ variant: 'success', title: 'Meta Graph API responded OK' });
      else toast({ variant: 'warning', title: 'Test failed', description: res.error });
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Test failed';
      setTestResult({ ok: false, error: msg });
      toast({ variant: 'error', title: 'Test failed', description: msg });
    } finally {
      setTesting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Deactivate WhatsApp? Outbound WhatsApp messages will stop immediately.')) return;
    try {
      await whatsappService.deactivateConfig();
      toast({ variant: 'success', title: 'WhatsApp credentials deactivated' });
      setExisting(null);
      await load();
    } catch (err) {
      toast({
        variant: 'error',
        title: 'Deactivation failed',
        description: err instanceof ApiError ? err.message : 'Could not deactivate',
      });
    }
  };

  const copyWebhook = async () => {
    if (!existing?.webhook_url) return;
    try {
      await navigator.clipboard.writeText(existing.webhook_url);
      toast({ variant: 'success', title: 'Webhook URL copied' });
    } catch {
      toast({ variant: 'warning', title: 'Clipboard unavailable', description: existing.webhook_url });
    }
  };

  return (
    <ErrorBoundary>
      <PageContainer>
        {/* Hero */}
        <div className="relative overflow-hidden bg-gradient-to-r from-success-600 via-teal-600 to-brand-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(5,150,105,0.55)] mb-6 animate-fade-in">
          <div className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-10 -left-10 h-40 w-40 rounded-full bg-white/5 blur-3xl" />
          <div className="relative flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-success-100 mb-1">Integrations · Notifications</div>
              <h1 className="text-2xl font-bold text-white">WhatsApp Business Cloud</h1>
              <p className="text-success-100 mt-1 text-sm max-w-2xl">
                Per-tenant Meta system-user credentials. Secrets are AES-256-GCM encrypted at rest;
                GET responses return only a <span className="font-mono">••••last4</span> preview.
                Webhooks are verified with HMAC-SHA256 using your App Secret.
              </p>
            </div>
            <WhatsAppGlyph />
          </div>
        </div>

        {/* Platform read-only banner */}
        {isPlatform && (
          <div className={cn(GLASS, 'p-4 mb-6 flex items-start gap-3 border-l-4 border-success-500')}>
            <div className="w-8 h-8 rounded-lg bg-success-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">i</div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white">Platform read-only</div>
              You're viewing this tenant's WhatsApp configuration for support purposes.
              Only the tenant <span className="font-semibold">OWNER</span> can save, rotate, or deactivate credentials.
            </div>
          </div>
        )}

        {/* Status cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <StatusCard existing={existing} loading={loading} />
          <WebhookCard existing={existing} onCopy={copyWebhook} />
          <TestCard existing={existing} testing={testing} onTest={handleTest} result={testResult} canEdit={canEdit} />
        </div>

        {/* Credentials form */}
        <div className={cn(GLASS_FORM, 'p-6 animate-slide-up')}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold uppercase tracking-[0.15em] text-neutral-500 dark:text-neutral-400">
              {existing ? 'Rotate credentials' : 'Configure credentials'}
            </h2>
            {!canEdit && (
              <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
                OWNER only
              </span>
            )}
          </div>
          {loading ? (
            <Skeleton className="h-64" />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Phone Number ID"
                placeholder="1234567890123456"
                value={phoneNumberId}
                onChange={(v: string) => setPhoneNumberId(v)}
                helperText="Meta App Dashboard → WhatsApp → API Setup → Phone number ID"
                disabled={!canEdit}
                className="font-mono tabular-nums"
              />
              <Input
                label="Business Account ID (optional)"
                placeholder="9876543210987654"
                value={businessAccountId}
                onChange={(v: string) => setBusinessAccountId(v)}
                helperText="Required for media uploads. Meta App Dashboard → Business Account ID"
                disabled={!canEdit}
                className="font-mono tabular-nums"
              />
              <div className="flex flex-col">
                <Input
                  label="System User Access Token"
                  type={showKey ? 'text' : 'password'}
                  placeholder="EAAG…"
                  value={apiKey}
                  onChange={(v: string) => setApiKey(v)}
                  helperText="Permanent system-user token from Meta Business Suite. Min 20 chars."
                  disabled={!canEdit}
                  className="font-mono"
                />
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setShowKey((v) => !v)}
                    className="text-xs text-success-600 dark:text-success-400 self-end mt-1 hover:underline"
                  >
                    {showKey ? 'Hide' : 'Show'} token while typing
                  </button>
                )}
              </div>
              <div className="flex flex-col">
                <Input
                  label="App Secret"
                  type={showSecret ? 'text' : 'password'}
                  placeholder="a1b2c3d4…"
                  value={appSecret}
                  onChange={(v: string) => setAppSecret(v)}
                  helperText="Used for webhook HMAC-SHA256 signature verification. Min 16 chars."
                  disabled={!canEdit}
                  className="font-mono"
                />
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => setShowSecret((v) => !v)}
                    className="text-xs text-success-600 dark:text-success-400 self-end mt-1 hover:underline"
                  >
                    {showSecret ? 'Hide' : 'Show'} secret while typing
                  </button>
                )}
              </div>
              <Input
                label="Webhook Verify Token"
                placeholder="my-verify-token"
                value={verifyToken}
                onChange={(v: string) => setVerifyToken(v)}
                helperText="Arbitrary string — must match the token you enter in Meta App webhook config."
                disabled={!canEdit}
                className="font-mono"
              />
              <div className="md:col-span-2 flex flex-wrap gap-3 items-center justify-end pt-2">
                {existing && canEdit && (
                  <Button variant="secondary" onClick={handleDeactivate}>
                    Deactivate
                  </Button>
                )}
                <Button
                  onClick={handleSave}
                  loading={saving}
                  disabled={!canEdit || !phoneNumberId || !apiKey || !appSecret || !verifyToken}
                >
                  {existing ? 'Rotate & save' : 'Save credentials'}
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Security notice */}
        <div className={cn(GLASS, 'p-5 mt-6 border-l-4 border-success-500')}>
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-success-500 text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">SEC</div>
            <div className="text-sm text-neutral-700 dark:text-neutral-300">
              <div className="font-semibold text-neutral-900 dark:text-white mb-1">Encrypted at rest</div>
              Access tokens, App Secrets, and Verify Tokens are encrypted with AES-256-GCM.
              Reads are Redis-cached for 5 minutes and invalidated on every rotation.
              Audit logs record who rotated, when — never the plaintext secret.
            </div>
          </div>
        </div>
      </PageContainer>
    </ErrorBoundary>
  );
}

// ─── Cards ────────────────────────────────────────────────────────────────

function StatusCard({ existing, loading }: { existing: WhatsAppConfigMasked | null; loading: boolean }) {
  const color = existing?.is_active
    ? 'from-success-500 to-teal-500'
    : existing
      ? 'from-warning-500 to-amber-500'
      : 'from-neutral-400 to-neutral-500';
  const pillTone = existing?.is_active
    ? 'bg-success-50 text-success-700 border-success-200 dark:bg-success-900/30 dark:text-success-300'
    : existing
      ? 'bg-warning-50 text-warning-700 border-warning-200 dark:bg-warning-900/30 dark:text-warning-300'
      : 'bg-neutral-100 text-neutral-700 border-neutral-200 dark:bg-neutral-800 dark:text-neutral-300';
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center text-white bg-gradient-to-br text-xs font-bold tracking-wider', color)} aria-hidden>WA</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Status</span>
      </div>
      {loading ? (
        <Skeleton className="h-8 w-32" />
      ) : existing ? (
        <div className="space-y-2">
          <div className="text-lg font-bold tabular-nums font-mono text-neutral-900 dark:text-white break-all">
            {existing.api_key_masked}
          </div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">
            Phone ID {existing.phone_number_id}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className={cn('inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider rounded-full border', pillTone)}>
              {existing.is_active ? 'Active' : 'Inactive'}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums">
              Updated {new Date(existing.updated_at).toLocaleString('en-IN')}
            </span>
          </div>
        </div>
      ) : (
        <div>
          <div className="text-lg font-semibold text-neutral-900 dark:text-white">Not configured</div>
          <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Outbound WhatsApp messages will fail until credentials are configured.
          </div>
        </div>
      )}
    </div>
  );
}

function WebhookCard({ existing, onCopy }: { existing: WhatsAppConfigMasked | null; onCopy: () => void }) {
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold tracking-wider" aria-hidden>WH</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Webhook URL</span>
      </div>
      {existing?.webhook_url ? (
        <>
          <div className="font-mono text-xs text-neutral-800 dark:text-neutral-200 break-all mb-3 p-2 rounded-md bg-neutral-50 dark:bg-neutral-800/60">
            {existing.webhook_url}
          </div>
          <Button size="sm" variant="secondary" onClick={onCopy} className="w-full">Copy webhook URL</Button>
        </>
      ) : (
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          Webhook URL will appear here after you save credentials. Register it in Meta App → Webhooks.
        </div>
      )}
    </div>
  );
}

function TestCard({
  existing, testing, onTest, result, canEdit,
}: {
  existing: WhatsAppConfigMasked | null;
  testing: boolean;
  onTest: () => void;
  result: { ok: boolean; error?: string } | null;
  canEdit: boolean;
}) {
  const last = existing?.last_tested_at
    ? `${existing.last_test_status ?? '—'} · ${new Date(existing.last_tested_at).toLocaleString('en-IN')}`
    : 'Never';
  return (
    <div className={cn(GLASS, 'p-5')}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 flex items-center justify-center text-white text-xs font-bold tracking-wider" aria-hidden>OK?</div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">Liveness</span>
      </div>
      <div className="text-xs text-neutral-600 dark:text-neutral-300 mb-2">
        Last tested <span className="tabular-nums font-mono">{last}</span>
      </div>
      {result && (
        <div className={cn(
          'text-xs mb-3 p-2 rounded-md',
          result.ok
            ? 'bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300'
            : 'bg-danger-50 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
        )}>
          {result.ok ? 'Meta Graph API responded OK' : (result.error || 'Test failed')}
        </div>
      )}
      <Button
        onClick={onTest}
        loading={testing}
        disabled={!existing || !canEdit}
        className="w-full"
        variant={existing ? 'primary' : 'secondary'}
      >
        Test connection
      </Button>
    </div>
  );
}

function WhatsAppGlyph() {
  return (
    <svg className="hidden md:block h-14 w-14 text-white/80" fill="none" viewBox="0 0 48 48" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="24" cy="24" r="18" />
      <path d="M16 28c1.5 2.5 4.5 5 8 5 4 0 7-2 8.5-5" />
      <path d="M16 20c1.5-2.5 4.5-5 8-5 4 0 7 2 8.5 5" />
      <path d="M12 32l2-6" />
      <path d="M12 32l-4 6 7-2" />
    </svg>
  );
}
